/**
 * Visual Config — Chart type metadata, capabilities, and helpers.
 *
 * Single source of truth for per-chart-type feature flags.
 * Adding a new chart type = one entry in CHART_TYPE_OPTIONS + one row
 * in CHART_CAPABILITIES.
 */
import {
  NumberSymbolRegular,
  DataLineRegular,
  DataAreaRegular,
  DataBarVerticalRegular,
  DataPieRegular,
  TableRegular,
  DataFunnelRegular,
  DataScatterRegular,
  BoardRegular,
  DataHistogramRegular,
} from '@fluentui/react-icons';
import type { ChartTypeOption, ChartTypeInfo, LabelPosition } from './types';

// ── Chart type options for the icon grid ───────────────────────────────────

export const CHART_TYPE_OPTIONS: ChartTypeInfo[] = [
  { value: 'kpi', label: 'KPI', icon: NumberSymbolRegular },
  { value: 'line', label: 'Line', icon: DataLineRegular },
  { value: 'area', label: 'Area', icon: DataAreaRegular },
  { value: 'bar', label: 'Bar', icon: DataBarVerticalRegular },
  { value: 'pie', label: 'Pie', icon: DataPieRegular },
  { value: 'table', label: 'Table', icon: TableRegular },
  { value: 'funnel', label: 'Funnel', icon: DataFunnelRegular },
  { value: 'scatter', label: 'Scatter', icon: DataScatterRegular },
  { value: 'heatmap', label: 'Heatmap', icon: BoardRegular },
  { value: 'histogram', label: 'Histogram', icon: DataHistogramRegular },
];

// ── Per-chart-type capabilities ────────────────────────────────────────────

/**
 * Feature capabilities for each chart type.
 * Controls which Visual Editor sections / controls are shown.
 */
export interface ChartTypeCapabilities {
  /** Has cartesian X/Y axes (shows Axis & Scale section) */
  axis: boolean;
  /** Supports stacked series (bar, line, area) */
  stacking: boolean;
  /** Shows percent labels (pie, funnel) */
  percent: boolean;
  /** Uses discrete color palette (false = continuous color scale, e.g. heatmap) */
  palette: boolean;
  /** Supports uniform vs per-category color mode (bar/histogram single series) */
  colorMode: boolean;
  /** Supports axis-trigger shared tooltip */
  sharedTooltip: boolean;
  /** Supports crosshair axis pointer */
  crosshair: boolean;
  /** Shows grid lines toggle */
  gridLines: boolean;
  /** Supports horizontal orientation (bar only) */
  horizontal: boolean;
  /** Shows scale radio (linear/log), start-at-zero, and reverse-axis controls */
  scaleControl: boolean;
  /** Shows discrete legend section (false = color is via visualMap/gradient) */
  legend: boolean;
  /** Both axes are category type — force all labels visible, never change axis type */
  categoryAxes: boolean;
  /** Shows the Chart Style section (line style, bar style, etc.) */
  chartStyle: boolean;
  /** Shows the Colors & Palette section */
  colors: boolean;
  /** Shows per-series color overrides (individual color pickers per series) */
  seriesColors: boolean;
  /** Shows the Data Labels section (show values, abbreviate, label position) */
  dataLabels: boolean;
  /** Whether the chart container renders the title header above the card body (false = title rendered inside the component, e.g. KPI StatCard) */
  titleInCard: boolean;
  /** Whether series name is derived from the single measure field label (e.g. KPI "Sum Amount") instead of data column names */
  singleSeriesLabel: boolean;
}

/**
 * Capability matrix — single source of truth.
 *
 * When adding a new chart type, add a row here and all Visual Editor
 * sections will automatically show/hide the correct controls.
 */
export const CHART_CAPABILITIES: Record<ChartTypeOption, ChartTypeCapabilities> = {
  //                axis   stack  pct    palette cMode  tooltip cross  grid   horiz  scale  legend catAxes style  colors sColor labels titleIn single
  kpi:       { axis: false, stacking: false, percent: false, palette: true,  colorMode: false, sharedTooltip: false, crosshair: false, gridLines: false, horizontal: false, scaleControl: false, legend: false, categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: false, titleInCard: false, singleSeriesLabel: true  },
  line:      { axis: true,  stacking: true,  percent: false, palette: true,  colorMode: false, sharedTooltip: true,  crosshair: true,  gridLines: true,  horizontal: false, scaleControl: true,  legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  area:      { axis: true,  stacking: true,  percent: false, palette: true,  colorMode: false, sharedTooltip: true,  crosshair: true,  gridLines: true,  horizontal: false, scaleControl: true,  legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  bar:       { axis: true,  stacking: true,  percent: false, palette: true,  colorMode: true,  sharedTooltip: true,  crosshair: true,  gridLines: true,  horizontal: true,  scaleControl: true,  legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  histogram: { axis: true,  stacking: false, percent: false, palette: true,  colorMode: true,  sharedTooltip: true,  crosshair: true,  gridLines: true,  horizontal: false, scaleControl: true,  legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  pie:       { axis: false, stacking: false, percent: true,  palette: true,  colorMode: false, sharedTooltip: false, crosshair: false, gridLines: false, horizontal: false, scaleControl: false, legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  table:     { axis: false, stacking: false, percent: false, palette: false, colorMode: false, sharedTooltip: false, crosshair: false, gridLines: false, horizontal: false, scaleControl: false, legend: false, categoryAxes: false, chartStyle: false, colors: false, seriesColors: false, dataLabels: false, titleInCard: true,  singleSeriesLabel: false },
  funnel:    { axis: false, stacking: false, percent: true,  palette: true,  colorMode: false, sharedTooltip: false, crosshair: false, gridLines: false, horizontal: false, scaleControl: false, legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  scatter:   { axis: true,  stacking: false, percent: false, palette: true,  colorMode: false, sharedTooltip: false, crosshair: false, gridLines: true,  horizontal: false, scaleControl: true,  legend: true,  categoryAxes: false, chartStyle: true,  colors: true,  seriesColors: true,  dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
  heatmap:   { axis: true,  stacking: false, percent: false, palette: true,  colorMode: false, sharedTooltip: true,  crosshair: false, gridLines: false, horizontal: false, scaleControl: false, legend: false, categoryAxes: true,  chartStyle: true,  colors: true,  seriesColors: false, dataLabels: true,  titleInCard: true,  singleSeriesLabel: false },
};

// ── Label position config per chart type ───────────────────────────────────

export interface LabelPositionConfig {
  /** Default position pre-selected in the UI */
  defaultPosition: LabelPosition;
  /** Available position options for the dropdown/radio group */
  positions: { value: LabelPosition; label: string }[];
}

export const LABEL_POSITION_CONFIG: Record<string, LabelPositionConfig> = {
  bar:       { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  line:      { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  area:      { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  scatter:   { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  pie:       { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  funnel:    { defaultPosition: 'inside',  positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  histogram: { defaultPosition: 'outside', positions: [{ value: 'inside', label: 'Inside' }, { value: 'outside', label: 'Outside' }] },
  heatmap:   { defaultPosition: 'inside',  positions: [{ value: 'inside', label: 'Inside' }] },
};

/** Get the label position config for a chart type, or undefined if not supported. */
export function getLabelPositionConfig(chartType: string): LabelPositionConfig | undefined {
  return LABEL_POSITION_CONFIG[chartType];
}

/** Get the default label position for a chart type. Falls back to 'outside'. */
export function getDefaultLabelPosition(chartType: string): LabelPosition {
  return LABEL_POSITION_CONFIG[chartType]?.defaultPosition ?? 'outside';
}

// ── Convenience accessors (backward-compatible) ────────────────────────────

/** Lookup capabilities for a chart type, with safe fallback. */
export function getCapabilities(chartType: string): ChartTypeCapabilities {
  return CHART_CAPABILITIES[chartType as ChartTypeOption] ?? CHART_CAPABILITIES.bar;
}

export function supportsAxis(chartType: string): boolean {
  return getCapabilities(chartType).axis;
}

export function supportsPercent(chartType: string): boolean {
  return getCapabilities(chartType).percent;
}

export function supportsStacking(chartType: string): boolean {
  return getCapabilities(chartType).stacking;
}